import { api, ApiError, setDeviceHeaders, type CurrentUser, type GetPaths } from '@/api/client';
import { create } from 'zustand';
import { queryClient } from '@/queryClient';
import { keepFiguresIn } from '@/stores/queryCache';
import { memoryHolder, startOutbox, stopOutbox } from './engine';
import { startWarming, stopWarming } from './warm';
import { PersonalStore, browserId, forgetEverything, type PersonSnapshot } from './personalStore';

/**
 * The open copy of the signed-in person's work, and what it takes to open it
 * (.docs/023, step 3).
 *
 * Online, the copy is opened with the person's server key, and wrapped again
 * for the password if one was just typed. With no connection it is opened with
 * the password alone. Either way, failing to open it never stops anybody
 * signing in online: the copy is what makes offline work possible, not what
 * makes the app work.
 */

let current: PersonalStore | null = null;

/** What the "This PC" page and the sign-out warning want to know. */
interface CopyState {
  /** Whether a copy is open for the person signed in. */
  open: boolean;
  /** Whether the browser promised not to clear it to make room. Null until asked. */
  kept: boolean | null;
}

export const useOfflineCopy = create<CopyState>(() => ({ open: false, kept: null }));

export function personalStore(): PersonalStore | null {
  return current;
}

function setCurrent(store: PersonalStore | null): void {
  if (current && current !== store) current.close();
  current = store;
  useOfflineCopy.setState({ open: store !== null });
  // What this person last saw comes back from their copy; at sign-out the
  // figures in memory go, so the next person at this window starts clean.
  void keepFiguresIn(store, queryClient);
  // Work saved here goes out from the copy it was saved in.
  if (store) startOutbox(store);
  else stopOutbox();
}

/**
 * Opens the copy once the server has confirmed the person. `password` is the
 * one just typed, or null on a reload.
 *
 * Answers with the server's words when this PC has been revoked: its copies
 * are wiped, and the caller ends the session with them.
 */
export async function openCopyOnline(user: CurrentUser, password: string | null): Promise<{ revoked: string | null }> {
  try {
    const { key } = (await api.get('/api/v1/settings/offline-key' as GetPaths)) as unknown as { key: string };
    setCurrent(await PersonalStore.signedInOnline(user as unknown as PersonSnapshot, password, key));
  } catch {
    // No copy this time: storage refused, or the key could not be had. Work
    // saved now waits in memory and goes while the page is open; offline
    // sign-in waits for a later try.
    startOutbox(memoryHolder());
    return { revoked: null };
  }
  const revoked = await registerBrowser();
  if (revoked) return { revoked };
  void askToKeep();
  // Everything the Inventory & Stores tabs need, loaded while the line holds
  // and kept fresh from the change feed.
  startWarming(current ?? undefined);
  return { revoked: null };
}

/** Opens the copy with no connection. Throws the reason, in words, when it cannot. */
export async function openCopyOffline(login: string, password: string): Promise<CurrentUser> {
  const { store, user } = await PersonalStore.signInOffline(login, password);
  setCurrent(store);
  setDeviceHeaders({ 'X-Device-Id': await browserId(), 'X-App-Version': __APP_VERSION__ });
  return user as unknown as CurrentUser;
}

/** Closes the copy at sign-out. It stays on the PC, sealed, for its owner. */
export function closeCopy(): void {
  stopWarming();
  setCurrent(null);
  setDeviceHeaders({});
}

/** Keeps the person's latest details in their copy, for the next offline sign-in. */
export function rememberInCopy(user: CurrentUser): void {
  void current?.write('user', user).catch(() => undefined);
}

/**
 * Tells the server which browser this is. Every request after carries the id,
 * so a revoked PC is turned away. Answers with the server's words when this
 * PC has been revoked, after wiping every copy it holds.
 */
async function registerBrowser(): Promise<string | null> {
  const deviceId = await browserId();
  setDeviceHeaders({ 'X-Device-Id': deviceId, 'X-App-Version': __APP_VERSION__ });
  try {
    await api.post('/api/v1/devices/register', { deviceId, name: browserName(), appVersion: __APP_VERSION__ });
    return null;
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) {
      setCurrent(null);
      setDeviceHeaders({});
      await forgetEverything().catch(() => undefined);
      return e.message;
    }
    // Not reachable, or some other trouble: tried again at the next sign-in.
    return null;
  }
}

/** Asks the browser not to clear the copy when the disk fills. */
async function askToKeep(): Promise<void> {
  try {
    const kept = (await navigator.storage?.persist?.()) ?? null;
    useOfflineCopy.setState({ kept });
  } catch {
    useOfflineCopy.setState({ kept: false });
  }
}

/** "Chrome on Windows": enough for an administrator to recognise the PC. */
function browserName(): string {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /Firefox\//.test(agent)
      ? 'Firefox'
      : /Chrome\//.test(agent)
        ? 'Chrome'
        : /Safari\//.test(agent)
          ? 'Safari'
          : 'A browser';
  const system = /Windows/.test(agent)
    ? 'Windows'
    : /Android/.test(agent)
      ? 'Android'
      : /Mac OS/.test(agent)
        ? 'Mac'
        : /Linux/.test(agent)
          ? 'Linux'
          : 'this PC';
  return `${browser} on ${system}`;
}
